import { useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Plus } from 'lucide-react';

import { PageHeader } from '@/ui/PageHeader';
import { Button } from '@/ui/base/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/base/tabs';

import {
  ComponentesRemuneracaoTab,
  type ComponentesRemuneracaoTabHandle,
} from './ComponentesRemuneracaoTab';
import { FaixasIrTab, type FaixasIrTabHandle } from './FaixasIrTab';
import {
  ParametrosRetencoesTab,
  type ParametrosRetencoesTabHandle,
} from './ParametrosRetencoesTab';

/**
 * `/operacao/financeiro/tabela-precos` — parametrização contratual
 * versionada por vigência, em três abas: componentes da remuneração,
 * parâmetros de retenções e faixas progressivas do IR.
 *
 * Dado simulado por decisão de produto (ver `_dev/cenarioDeTela.ts`): a API
 * do módulo financeiro ainda não existe. Cada aba mantém seu próprio CRUD em
 * memória — ver os arquivos das abas.
 *
 * A aba ativa vive na URL (`?aba=precos|retencoes|ir`) para poder ser
 * compartilhada/recarregada sem perder o lugar; a rota em si não muda.
 */

/**
 * Nome canônico da tela, o mesmo em todo caminho de navegação: item do menu e
 * cartões do hub de Parametrização saem de `nucleo/navegacao/mapa.ts`, e as
 * migalhas derivam do mesmo item.
 */
const TITULO = 'Tabela de Preços Contratuais';

/**
 * Descrição do cabeçalho, cobrindo as três abas da tela — não só a primeira.
 */
const DESCRICAO =
  'Parâmetros contratuais do cálculo de remuneração dos credenciados do TEG, em três abas: componentes da remuneração por tipo de bancada, parâmetros de retenções e faixas progressivas do IR. Cada conjunto é versionado por vigência.';

type Aba = 'precos' | 'retencoes' | 'ir';

const ABAS_VALIDAS: Aba[] = ['precos', 'retencoes', 'ir'];
const ABA_PADRAO: Aba = 'precos';

function lerAba(parametros: URLSearchParams): Aba {
  const valor = parametros.get('aba');
  return (ABAS_VALIDAS as string[]).includes(valor ?? '') ? (valor as Aba) : ABA_PADRAO;
}

export function TabelaDePrecosPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const abaAtual = lerAba(searchParams);

  const setAba = (proxima: string) => {
    setSearchParams(
      (atual) => {
        const novo = new URLSearchParams(atual);
        if (proxima === ABA_PADRAO) {
          novo.delete('aba');
        } else {
          novo.set('aba', proxima);
        }
        return novo;
      },
      { replace: true },
    );
  };

  const precosRef = useRef<ComponentesRemuneracaoTabHandle>(null);
  const retencoesRef = useRef<ParametrosRetencoesTabHandle>(null);
  const irRef = useRef<FaixasIrTabHandle>(null);

  const acaoDoHeader =
    abaAtual === 'precos' ? (
      <Button onClick={() => precosRef.current?.abrirCriacao()}>
        <Plus className="h-4 w-4" /> Nova versão
      </Button>
    ) : abaAtual === 'retencoes' ? (
      <Button onClick={() => retencoesRef.current?.abrirCriacao()}>
        <Plus className="h-4 w-4" /> Nova versão
      </Button>
    ) : (
      <Button onClick={() => irRef.current?.abrirCriacao()}>
        <Plus className="h-4 w-4" /> Nova faixa
      </Button>
    );

  return (
    <div className="space-y-6">
      <PageHeader title={TITULO} description={DESCRICAO}>
        {acaoDoHeader}
      </PageHeader>

      <Tabs value={abaAtual} onValueChange={setAba}>
        <TabsList>
          <TabsTrigger value="precos">Componentes da Remuneração</TabsTrigger>
          <TabsTrigger value="retencoes">Parâmetros de Retenções</TabsTrigger>
          <TabsTrigger value="ir">Faixas Progressivas do IR</TabsTrigger>
        </TabsList>

        <TabsContent value="precos">
          <ComponentesRemuneracaoTab ref={precosRef} />
        </TabsContent>
        <TabsContent value="retencoes">
          <ParametrosRetencoesTab ref={retencoesRef} />
        </TabsContent>
        <TabsContent value="ir">
          <FaixasIrTab ref={irRef} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default TabelaDePrecosPage;
